<template>
  <StepperHeader :current="3" />
  <RegisterLayout>
    <template #description>
      <h2>Finalmente, crea tu propio dominio personalizado</h2>
      <p>Podrás acceder a tu equipo de trabajo desde la URL que generes.</p>

      <section class="drawing">
        <img :src="tenantImage" height="300" alt="rocket" />
      </section>
    </template>

    <template #form>
      <FormCard title="¡Personaliza la URL  para el equipo de trabajo de tu empresa!" hide-img>
        <div class="info">
          <InfoCircleOutlined class="info__icon" />
          <p class="info__text">Recuerda que esto no se podrá cambiar más adelante.</p>
        </div>

        <a-form class="form" ref="formRef" layout="vertical" :model="formState" :rules="rules">
          <a-form-item label="Nombre de tu equipo de trabajo" name="name" required>
            <a-input
              v-model:value="formState.name"
              placeholder="Escribe aquí..."
              :addon-after="domain"
              @input="handleNameWork"
            />
            <span class="ant-form-item-explain-error" v-if="messageError">{{messageError}}</span>
          </a-form-item>
          <p class="example">Ejemplo: banconacional{{ domain }}</p>
        </a-form>

        <p class="recaptcha-policies">
          This site is protected by reCAPTCHA and the Google
          <a href="https://policies.google.com/privacy">Privacy Policy</a> and
          <a href="https://policies.google.com/terms">Terms of Service</a> apply.
        </p>

        <div class="columns columns__buttons">
          <a-button class="button" @click="handleBackStep"> Volver </a-button>

          <a-button
            class="button"
            type="primary"
            id="workspace-generate-btn"
            @click="handleSubmit"
            :disabled="!isFormValid"
            :loading="loading"
          >
            {{ loading ? "Generando espacio de trabajo" : "Generar espacio de trabajo" }}
          </a-button>
        </div>
      </FormCard>
    </template>
  </RegisterLayout>

  <creating-business-modal v-if="loading" @onCloseModal="handleCloseModal" />
  <preview-create-business-modal
    v-model:visible="showPreviewBusiness"
    :plan="customer"
    @registerCustomer="registerCustomer(nameWork)"
  />
  <!-- <div>
    <main-logo-register />
    <div class="steps">
      <a-steps :current="3" size="small">
        <a-step title="Crea tu cuenta" description="Terminado" />
        <a-step title="Crea tu contraseña" description="Terminado" />
        <a-step title="Tu empresa" description="Terminado" />
        <a-step title="Crea tu URL personalizada" description="En proceso" />
      </a-steps>
    </div>
  </div> -->
</template>

<script>
import { defineComponent, reactive, ref, watch } from "vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";

import httpManager from "@/app/shared/utils/httpManager";

import CreatingBusinessModal from "../../../register/components/modals/CreatingBusinessModal.vue";
import PreviewCreateBusinessModal from "../../../register/components/modals/PreviewCreateBusinessModal.vue";
import StepperHeader from "../header/StepperHeader.vue";
import RegisterLayout from "../layout/RegisterLayout.vue";
import FormCard from "@/app/auth/components/atoms/FormCard.vue";

import tenantImage from "@/app/register/assets/tenant.svg?url";
import { customerPlanByToken } from "@/app/register/services";
import { validateTenantName } from "@/app/register/services/index";
import { debounce } from "lodash";
import { PlanPayType } from "@/app/common/types";

export default defineComponent({
  components: {
    InfoCircleOutlined,
    CreatingBusinessModal,
    StepperHeader,
    RegisterLayout,
    FormCard,
    PreviewCreateBusinessModal,
  },
  setup() {
    const formRef = ref();

    const domain = ".beexcontact.app/";

    const formState = reactive({
      name: "",
    });
    const nameWork = ref("");
    const isFormValid = ref(false);
    const messageError = ref();

    const rules = {
      name: [
        { required: true, message: "Este campo es requerido" },
        {
          pattern: /^[a-zA-Z0-9-]{1,63}$/,
          message: "Solo se permiten caracteres alfanuméricos o guiones (1-63 caracteres)",
        },
      ]
    };

    watch(
      () => formState.name,
      debounce(async () => {
        messageError.value = null;
        if (formRef.value) {
          try {
            isFormValid.value = false;
            await formRef.value.validate();
            const validate = await validateTenantName({ workspace: formState.name });
            if (validate.exists) {
              messageError.value = "Tenant ya existente";
            }
            isFormValid.value = !validate?.exists;
          } catch {
            isFormValid.value = false;
          }
        }
      }, 300)
    );

    return {
      nameWork,
      rules,
      formRef,
      formState,
      domain,
      tenantImage,
      isFormValid,
      messageError,
    };
  },
  data() {
    return {
      loading: false,
      showPreviewBusiness: false,
      customer: null,
    };
  },
  methods: {
    async handleSubmit() {
      try {
        await this.formRef
          .validateFields()
          .then(async () => {
            if (this.customer.type_plan_license == PlanPayType.Prepaid ||
              (this.customer.type_plan_license == PlanPayType.Postpaid && 
              this.customer?.configuration.high_price?.value > 0)
            ) {
              this.showPreviewBusiness = true;  
            } else {
              await this.registerCustomer(this.nameWork);
            }
          })
          .catch((error) => {
            console.error("error", error);
          });
      } catch (error) {
        console.error("error", error);
        this.loading = false;
        this.$message.error("Ya existe el equipo de trabajo con ese nombre", 5);
      } finally {
        this.loading = false;
      }
    },
    async registerCustomer(workspace) {
      try {
        this.loading = true;
        await this.$store.dispatch("createNewCustomer", { workspace, customToken: this.$route.query.custom_token });
        httpManager.updateBaseURL(`https://${workspace}.backend.${import.meta.env.VITE_HOSTNAME}`);
        await this.$store.dispatch("loginInRegister");
        await this.$store.dispatch("removeTemporalUserFromStore");
        localStorage.setItem("workspace", workspace);
        if (window.location.hostname == "localhost") {
          this.$router.push(`/login`);
        } else {
          localStorage.clear();
          window.location.href = `https://${workspace}.${import.meta.env.VITE_HOSTNAME}/login`;
        }
      } catch (error) {
        console.error(error);
        this.loading = false;
        this.$message.error("Ya existe el equipo de trabajo con ese nombre", 5);
      }
    },
    handleNameWork(event) {
      this.nameWork = event.target.value.replace(/[^A-Za-z0-9]/g, "");
    },
    handleCloseModal() {
      this.visible = false;
    },
    handleBackStep() {
      this.$store.dispatch("setRegisterWorkspaceStep", 3);
    },
    async handleCustomerPlanByToken() {
      try {
        const token = this.$route.query.custom_token;
        const response = await customerPlanByToken(token);
        this.customer = response;
        console.log(response, 'validate token');
      } catch {
        // this.$router.push("/register");
      }
    },
  },
  created() {
    console.log('created register workspace template');
    this.handleCustomerPlanByToken();
  },
});
</script>

<style lang="sass" scoped>
.container
  width: 30%
  margin-top: 40px
  margin-left: 11%

.drawing
  text-align: center
  margin-top: 2rem

.text__work
  margin-top: -10px
  margin-bottom: 60px

.name__group
  margin-top: 10px
  color: $gray-6

.text__url
  color: $gray-9

.strong
  color: $gray-1

.container__info
  margin: 0 auto
  margin-top: 200px
  margin-bottom: 50px

.container__button
  width: 100%

.button
  width: 100%

.flex
  display: flex

.url
  margin-left: 5px
  margin-top: 10px

.name
  font-weight: 600
  font-size: 30px

.subtitle
  font-size: 16px
  display: flex
  margin-top: 20px

.backStep
  margin-right: 10px

.columns
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 0 12px

  &__buttons
    grid-template-columns: 120px 1fr

.info
  color: $gray-8
  display: inline-flex
  margin: 24px 0 12px

  &__icon
    margin-top: 4px
    margin-right: 8px

  &__text
    font-style: italic
    font-weight: 300

.example
  color: $gray-6

.ant-form-item
  margin-bottom: 12px

.form
  margin-bottom: 28px

.recaptcha-policies
  margin-top: 20px
  color: $text-tertiary
  margin-bottom: 8px
  font-size: 12px
</style>
